/**
 * databaseAuditSlice
 * ===========================================================================
 * Redux state for the admin System-Management / database-audit tooling
 * (server routes under /api/database-audit/*). Consolidates the reads the
 * System Management console panels used to do with ad-hoc apiClient.get + local
 * useState, so they get consistent loading/error/caching like the rest of the
 * app's slices.
 *
 * Per-resource shape: { data, loading, error }. s3Reclaim is an action result.
 * One-off, non-audit actions (e.g. DELETE /delete/user/:id in the Reset panel)
 * intentionally stay as direct apiClient calls — they aren't database-audit
 * endpoints and don't need cached state.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../apiTools/apiClient';

const BASE = '/database-audit';
const errMsg = (e) => e.response?.data?.error || e.response?.data?.message || e.message;

export const fetchDbHealth = createAsyncThunk(
  'databaseAudit/fetchHealth',
  async (_, { rejectWithValue }) => {
    try { return (await apiClient.get(`${BASE}/health`)).data; }
    catch (e) { return rejectWithValue(errMsg(e)); }
  }
);

export const fetchDbStatistics = createAsyncThunk(
  'databaseAudit/fetchStatistics',
  async (_, { rejectWithValue }) => {
    try { return (await apiClient.get(`${BASE}/statistics`)).data; }
    catch (e) { return rejectWithValue(errMsg(e)); }
  }
);

export const fetchResetPreview = createAsyncThunk(
  'databaseAudit/fetchResetPreview',
  async ({ protectGuests = true } = {}, { rejectWithValue }) => {
    try { return (await apiClient.get(`${BASE}/reset-preview`, { params: { protectGuests } })).data; }
    catch (e) { return rejectWithValue(errMsg(e)); }
  }
);

export const fetchDeletionAudit = createAsyncThunk(
  'databaseAudit/fetchDeletionAudit',
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try { return (await apiClient.get(`${BASE}/deletion-audit`, { params: { limit } })).data; }
    catch (e) { return rejectWithValue(errMsg(e)); }
  }
);

export const fetchS3Reconcile = createAsyncThunk(
  'databaseAudit/fetchS3Reconcile',
  async ({ deep = true } = {}, { rejectWithValue }) => {
    try { return (await apiClient.get(`${BASE}/s3-reconcile`, { params: { deep } })).data; }
    catch (e) { return rejectWithValue(errMsg(e)); }
  }
);

export const reclaimS3 = createAsyncThunk(
  'databaseAudit/reclaimS3',
  async ({ prefixes, deadUsers, confirm } = {}, { rejectWithValue }) => {
    try { return (await apiClient.post(`${BASE}/s3-reclaim`, { prefixes, deadUsers, confirm })).data; }
    catch (e) { return rejectWithValue(errMsg(e)); }
  }
);

const resource = () => ({ data: null, loading: false, error: null });
const initialState = {
  health: resource(),
  statistics: resource(),
  resetPreview: resource(),
  deletionAudit: resource(),
  s3Reconcile: resource(),
  s3Reclaim: { result: null, loading: false, error: null },
};

// Wire the standard pending/fulfilled/rejected trio for a { data, loading, error } key.
function wire(builder, thunk, key) {
  builder
    .addCase(thunk.pending, (s) => { s[key].loading = true; s[key].error = null; })
    .addCase(thunk.fulfilled, (s, a) => { s[key].loading = false; s[key].data = a.payload; })
    .addCase(thunk.rejected, (s, a) => { s[key].loading = false; s[key].error = a.payload || a.error?.message || 'Request failed'; });
}

const slice = createSlice({
  name: 'databaseAudit',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    wire(builder, fetchDbHealth, 'health');
    wire(builder, fetchDbStatistics, 'statistics');
    wire(builder, fetchResetPreview, 'resetPreview');
    wire(builder, fetchDeletionAudit, 'deletionAudit');
    wire(builder, fetchS3Reconcile, 's3Reconcile');
    builder
      .addCase(reclaimS3.pending, (s) => { s.s3Reclaim.loading = true; s.s3Reclaim.error = null; })
      .addCase(reclaimS3.fulfilled, (s, a) => { s.s3Reclaim.loading = false; s.s3Reclaim.result = a.payload; })
      .addCase(reclaimS3.rejected, (s, a) => { s.s3Reclaim.loading = false; s.s3Reclaim.error = a.payload || a.error?.message || 'Reclaim failed'; });
  },
});

export default slice.reducer;

const root = (state) => state.databaseAudit || initialState;
export const selectDbHealth = (s) => root(s).health;
export const selectDbStatistics = (s) => root(s).statistics;
export const selectResetPreview = (s) => root(s).resetPreview;
export const selectDeletionAudit = (s) => root(s).deletionAudit;
export const selectS3Reconcile = (s) => root(s).s3Reconcile;
export const selectS3Reclaim = (s) => root(s).s3Reclaim;
